// angular
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
// libs
import { CookieService } from 'ngx-cookie-service';
// shared
import { SharedModule } from '@shared/shared.module';
import {
  ApiSessionKeyGuard,
  CieUtilsModalManagerService, ERROR_HANDLER_TYPES,
  MODAL_MANAGER_CONFIG,
  NgCakeItEasyUtilsModule, SentryLoggerService,
  SessionKeeperModule,
  USERS_PROFILE_SERVICE_TOKEN
} from '@cakeiteasy/web-utils';
import { TranslatesService } from './translates';
// components
import { AppRoutes } from './app.routing';
import { AppComponent } from './app.component';
import { LocaleHttpInterceptor } from '@shared/intercepters/locale-http-interceptor';
import { CakeItEasyWebUiModule, UiCustomToasterComponent } from '@cakeiteasy/web-ui';
import { UiModule } from './ui/ui.module';
import { WrapperComponent } from './components/wrapper/wrapper.component';
import { ModalsModule } from './modals/modals.module';
import { ToastrModule } from 'ngx-toastr';
import { EditingCakeSessionService } from './shared/services/editing-cake-session.service';
import { TranslateModule } from '@ngx-translate/core';
import {
  CakeBuilderModule,
  CieWebCartModule,
  UserModule,
  GiftCardsManagerService, CART_ANALYTIC_TRACKERS,
  PostHogTrackerService,
} from '@cakeiteasy/order-process';
import { ContextProviderService } from '@shared/services/context-provider.service';
import { LocationManagerService } from '@cakeiteasy/web-utils';
import { CustomMetaGuard } from '@shared/shared-meta/custom-meta.guard';
import { CartHttpInterceptor } from '@shared/intercepters/cart-http-interceptor';
import * as Raven from 'raven-js';
import { AnalyticTracker } from '@analytic/services/analytic-tracker.service';

export function initLanguage(translateService: TranslatesService): Function {
  return (): Promise<any> => translateService.initLanguage();
}

export function initContext(contextService: ContextProviderService,
                            locationManager: LocationManagerService,
                            appConfig: any): Function {
  return (): Promise<boolean> => {
    contextService.countryCode$.next(appConfig.countryDomainsMap[locationManager.getDomainName()]);
    return Promise.resolve(true);
  };
}

export function profileService(locationManager: LocationManagerService,
                               appConfig: any) {
  return {
    getCountryCode: () => Promise.resolve(appConfig.countryDomainsMap[locationManager.getDomainName()])
  };
}

@NgModule({
  imports: [
    BrowserModule,
    HttpClientModule,
    RouterModule,
    AppRoutes,
    SharedModule.forRoot(),
    ToastrModule.forRoot({
      toastComponent: UiCustomToasterComponent,
      iconClasses: {
        error: 'error text-danger',
        success: 'success text-primary',
        info: 'info text-primary',
        warning: 'warning text-warning',
      },
    }),
    ModalsModule.forRoot(),
    NgCakeItEasyUtilsModule,
    CakeItEasyWebUiModule,
    TranslateModule,
    CakeBuilderModule,
    UiModule,
    SessionKeeperModule,
    UserModule.forRoot(),
    CieWebCartModule.forRoot(),
  ],
  declarations: [AppComponent, WrapperComponent],
  providers: [
    GiftCardsManagerService,
    CookieService,
    EditingCakeSessionService,
    CieUtilsModalManagerService,
    ApiSessionKeyGuard,
    CustomMetaGuard,
    // Analytics block
    AnalyticTracker,
    PostHogTrackerService,
    {
      provide: CART_ANALYTIC_TRACKERS,
      useClass: AnalyticTracker,
      multi: true,
    },
    // --------------------
    { provide: APP_INITIALIZER, useFactory: initLanguage, multi: true, deps: [TranslatesService] },
    {
      provide: APP_INITIALIZER,
      useFactory: initContext,
      multi: true,
      deps: [ContextProviderService, LocationManagerService, 'AppConfig'],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LocaleHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CartHttpInterceptor,
      multi: true,
    },
    {
      provide: MODAL_MANAGER_CONFIG,
      useValue: {
        isUseHash: true,
      },
    },
    { provide: SentryLoggerService, useFactory: () => new SentryLoggerService(Raven, ERROR_HANDLER_TYPES.RAVEN) },
    {
      provide: USERS_PROFILE_SERVICE_TOKEN,
      useFactory: profileService,
      deps: [LocationManagerService, 'AppConfig'],
    },
  ],
})
export class AppModule {}
